/**
 * internals.jsx — Helpers compartilhados pelo Compound DetailPanel.
 *
 * Tudo que era helper/sub-componente "privado" do DetailPanel monolítico mora
 * aqui pra evitar circular import entre tabs e index. Nada exportado daqui é
 * pra ser usado fora do diretório DetailPanel/.
 */
import { useEffect, useRef, useState } from 'react';

// Altura dos line charts dentro do DetailPanel (Tendência 7 dias, Últimos
// 30 dias, etc). 80px = compacto + clareza pra ver tendência + tooltip cabe
// com folga. 100px ficou alto demais visualmente.
export const CHART_HEIGHT = 80;

export const DETAIL_TABS = [
    { id: 'overview', label: 'Visão geral' },
    { id: 'vendas', label: 'Vendas' },
    { id: 'estoque', label: 'Estoque' },
    { id: 'cadastro', label: 'Cadastro' },
    { id: 'outbounds', label: 'Outbounds' },
    { id: 'inbounds', label: 'Inbounds' },
];

export const DETAIL_TAB_STORAGE_KEY = 'op:detail:lastTab';
export const VALID_TAB_IDS = new Set(['overview', 'estoque', 'vendas', 'cadastro', 'outbounds', 'inbounds']);

export const OUTBOUND_STATUS_LABEL = {
    pending: 'Solicitação',
    idle: 'Aguardando separação',
    picking: 'Em separação',
    ready_to_collect: 'Pronto para coleta',
    collected: 'Coletado',
};

export const PAGE_SIZE_OPTS_DETAIL = [25, 50, 100, 200];

export function LoadLastTab() {
    try {
        const stored = localStorage.getItem(DETAIL_TAB_STORAGE_KEY);
        return stored && VALID_TAB_IDS.has(stored) ? stored : 'overview';
    } catch {
        return 'overview';
    }
}

/**
 * Fetcher lazy: dispara somente quando `enabled=true` e `key` muda.
 * O fetcher é guardado em ref para não entrar como dependency e evitar
 * refetch infinito.
 */
export function useLazyFetch({ enabled, key, fetcher }) {
    const [state, setState] = useState({ key: null, loading: false, data: null, error: null });
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    useEffect(() => {
        if (!enabled || !key) {
            return undefined;
        }
        let cancelled = false;
        setState({ key, loading: true, data: null, error: null });
        Promise.resolve()
            .then(() => fetcherRef.current())
            .then((data) => {
                if (cancelled) {
                    return;
                }
                setState({ key, loading: false, data, error: null });
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                setState({ key, loading: false, data: null, error: err?.message || 'Erro ao carregar' });
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, key]);
    return state;
}

export function FormatDate(value) {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    // timeZone explícito (não depende do fuso do browser) — convenção do
    // projeto: backend serializa UTC, frontend SEMPRE converte pra Brasília.
    return date.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

export function StatusMsg({ kind, T, children }) {
    const color = kind === 'error' ? T.warn : T.muted;
    return <div style={{ padding: '24px 0', fontSize: 12, color, textAlign: 'center' }}>{children}</div>;
}

export function CadastroField({ label, value, mono = true, uppercase = false, T }) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                padding: '8px 12px',
                fontSize: 12,
                background: T.bg,
                borderBottom: `1px solid ${T.line}`,
            }}
        >
            <span style={{ color: T.muted }}>{label}</span>
            <span
                style={{
                    fontFamily: mono ? T.mono : T.sans,
                    color: T.ink,
                    textTransform: uppercase ? 'uppercase' : 'none',
                }}
            >
                {value || '—'}
            </span>
        </div>
    );
}

export function Pager({ total, page, pageSize, onPageChange, onPageSizeChange, T }) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const fromIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const toIdx = Math.min(safePage * pageSize, total);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 4px 4px',
                fontSize: 11,
                color: T.muted,
                fontFamily: T.sans,
            }}
        >
            <span style={{ fontFamily: T.mono }}>
                {fromIdx}–{toIdx} de <b style={{ color: T.ink }}>{total}</b>
            </span>
            <div style={{ flex: 1 }} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span>Linhas:</span>
                <select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    style={{
                        padding: '2px 4px',
                        borderRadius: 4,
                        border: `1px solid ${T.line}`,
                        background: T.bg,
                        color: T.ink,
                        fontSize: 11,
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                >
                    {PAGE_SIZE_OPTS_DETAIL.map((n) => (
                        <option key={n} value={n}>
                            {n}
                        </option>
                    ))}
                </select>
            </label>
            <button
                type="button"
                onClick={() => onPageChange(Math.max(1, safePage - 1))}
                disabled={safePage <= 1}
                style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: `1px solid ${T.line}`,
                    background: T.bg,
                    color: T.ink,
                    fontSize: 11,
                    cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                    opacity: safePage <= 1 ? 0.4 : 1,
                    outline: 'none',
                }}
            >
                ‹
            </button>
            <span style={{ fontFamily: T.mono, color: T.ink }}>
                {safePage} / {totalPages}
            </span>
            <button
                type="button"
                onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
                disabled={safePage >= totalPages}
                style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: `1px solid ${T.line}`,
                    background: T.bg,
                    color: T.ink,
                    fontSize: 11,
                    cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                    opacity: safePage >= totalPages ? 0.4 : 1,
                    outline: 'none',
                }}
            >
                ›
            </button>
        </div>
    );
}
